import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useDiscreet } from "@/context/DiscreetContext";
import { Eye, EyeOff, Menu, Sparkles, Users, Calendar, FileText, Gift, BookOpen, LayoutDashboard, Settings, ShieldCheck, LogOut, Search, Brain, Heart, Clock, CreditCard } from "lucide-react";
import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

function AmbassadorChip() {
  const { ambassador } = useAuth();
  if (!ambassador?.is_ambassador) return null;
  const days = ambassador.days_remaining;
  return (
    <Link
      to="/ambassador"
      data-testid="ambassador-chip"
      title={ambassador.expired ? "Evaluation complete · open portal" : "Ambassador portal"}
      className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[11px] tracking-wider uppercase border border-mauve/40 bg-mauve/8 text-mauve hover:bg-mauve/15 transition-colors"
    >
      <Heart size={12} />
      <span className="font-medium">Ambassador</span>
      {typeof days === "number" && !ambassador.expired && (
        <span className="text-cocoa/55 normal-case tracking-normal">· {days}d left</span>
      )}
      {ambassador.expired && <span className="text-cocoa/55 normal-case tracking-normal">· thank you</span>}
    </Link>
  );
}

function Logo({ small }) {
  return (
    <Link to="/" data-testid="header-home-link" className="flex items-center gap-2">
      <span className={`font-heading ${small ? "text-2xl" : "text-[1.75rem] md:text-3xl"} text-cocoa tracking-tight`}>
        Sugar<span className="italic font-medium text-mauve">Sense</span>
      </span>
    </Link>
  );
}

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/ask", label: "Ask SugarSense", icon: Sparkles, testid: "nav-ask" },
  { to: "/history", label: "History", icon: Clock, testid: "nav-history" },
  { to: "/self-insight", label: "Self-Insight", icon: Brain, testid: "nav-self-insight" },
  { to: "/intelligence", label: "Intelligence", icon: Brain, testid: "nav-intelligence" },
  { to: "/profiles", label: "Profiles", icon: Users, testid: "nav-profiles" },
  { to: "/timeline", label: "Timeline", icon: Calendar, testid: "nav-timeline" },
  { to: "/tracker", label: "Tracker", icon: Gift, testid: "nav-tracker" },
  { to: "/drafts", label: "Drafts", icon: FileText, testid: "nav-drafts" },
  { to: "/archetypes", label: "Archetypes", icon: BookOpen, testid: "nav-archetypes" },
];

export function Header() {
  const { user, logout } = useAuth();
  const { discreet, toggle } = useDiscreet();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  // Keyboard shortcut: Ctrl/Cmd+B toggles Discreet mode on every authed screen.
  useEffect(() => {
    if (!user) return;
    const handler = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if ((e.key || "").toLowerCase() === "b") {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [user, toggle]);

  const handleLogout = async () => { await logout(); nav("/"); };

  return (
    <header className="sticky top-0 z-40 bg-ivory/85 backdrop-blur-md border-b border-champagne">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Logo />
        {user ? (
          <>
            <nav className="hidden xl:flex items-center gap-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} data-testid={n.testid}
                  className={({ isActive }) => `px-3 py-2 text-sm rounded-full whitespace-nowrap transition-colors ${isActive ? "bg-cocoa text-ivory" : "text-cocoa/70 hover:text-cocoa hover:bg-champagne"}`}>
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="flex items-center gap-2">
              <AmbassadorChip />
              <button onClick={toggle} aria-label={discreet ? "Show content (Ctrl/Cmd+B)" : "Hide content (Ctrl/Cmd+B)"}
                title={discreet ? "Discreet mode ON · Ctrl/Cmd+B to reveal" : "One-tap blur · Ctrl/Cmd+B to hide"}
                data-testid="discreet-toggle"
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full transition-colors text-xs font-medium ${discreet ? "bg-mauve text-ivory" : "text-cocoa/70 hover:bg-champagne border border-champagne"}`}>
                {discreet ? <Eye size={14} /> : <EyeOff size={14} />}
                <span className="hidden sm:inline">{discreet ? "Hidden" : "Hide"}</span>
              </button>
              <Link to="/account" data-testid="nav-account" className="hidden md:inline-flex p-2 rounded-full text-cocoa/70 hover:bg-champagne">
                <Settings size={16} />
              </Link>
              {user.role === "admin" && (
                <Link to="/admin-ss" data-testid="nav-admin" className="hidden md:inline-flex p-2 rounded-full text-cocoa/70 hover:bg-champagne" title="Admin">
                  <ShieldCheck size={16} />
                </Link>
              )}
              <button onClick={handleLogout} data-testid="logout-btn"
                className="hidden md:inline-flex p-2 rounded-full text-cocoa/70 hover:bg-champagne">
                <LogOut size={16} />
              </button>
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <button data-testid="mobile-menu-trigger" aria-label="Open menu" className="xl:hidden p-2 -mr-2 text-cocoa hover:text-mauve"><Menu size={22} /></button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[85%] max-w-sm bg-ivory border-l border-champagne text-cocoa p-0 flex flex-col">
                  <div className="px-5 py-5 border-b border-champagne"><Logo /></div>
                  <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                    {NAV.map((n) => (
                      <NavLink key={n.to} to={n.to} data-testid={`mobile-${n.testid}`}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-full text-sm transition-colors ${isActive ? "bg-cocoa text-ivory" : "text-cocoa/80 hover:bg-champagne"}`}>
                        <n.icon size={16} /> {n.label}
                      </NavLink>
                    ))}
                    <NavLink to="/account" onClick={() => setOpen(false)} data-testid="mobile-nav-account"
                      className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-full text-sm ${isActive ? "bg-cocoa text-ivory" : "text-cocoa/80 hover:bg-champagne"}`}>
                      <Settings size={16} /> Account
                    </NavLink>
                    <NavLink to="/billing" onClick={() => setOpen(false)} data-testid="mobile-nav-billing"
                      className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-full text-sm ${isActive ? "bg-cocoa text-ivory" : "text-cocoa/80 hover:bg-champagne"}`}>
                      <CreditCard size={16} /> Billing
                    </NavLink>
                  </nav>
                  <div className="p-3 border-t border-champagne">
                    <button onClick={handleLogout} data-testid="mobile-logout-btn"
                      className="w-full ss-pill ss-pill-ghost h-11">
                      <LogOut size={14} /> Sign out
                    </button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </>
        ) : (
          <>
            <nav className="hidden lg:flex items-center gap-4 whitespace-nowrap">
              <Link to="/" data-testid="nav-home-link" className="text-sm text-cocoa/70 hover:text-cocoa">Home</Link>
              <Link to="/essays" className="text-sm text-cocoa/70 hover:text-cocoa">Editorial</Link>
              <Link to="/archetypes" className="text-sm text-cocoa/70 hover:text-cocoa">Archetypes</Link>
              <Link to="/research" data-testid="header-research-link" className="text-sm text-cocoa/70 hover:text-cocoa">Research</Link>
              <Link to="/pricing" className="text-sm text-cocoa/70 hover:text-cocoa">Pricing</Link>
              <Link to="/contact" data-testid="header-contact-link" className="text-sm text-cocoa/70 hover:text-cocoa">Contact</Link>
              <Link to="/search" data-testid="header-search-link" className="text-cocoa/70 hover:text-cocoa" aria-label="Search">
                <Search size={16} />
              </Link>
              <Link to="/login" data-testid="header-login-link" className="text-sm text-cocoa/70 hover:text-cocoa">Sign in</Link>
              <Link to="/register"><button data-testid="header-register-btn" className="ss-pill ss-pill-gold">Get started</button></Link>
            </nav>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="public-mobile-menu-trigger" aria-label="Open menu" className="lg:hidden p-2 -mr-2 text-cocoa hover:text-mauve"><Menu size={22} /></button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85%] max-w-sm bg-ivory border-l border-champagne text-cocoa p-0 flex flex-col">
                <div className="px-5 py-5 border-b border-champagne"><Logo /></div>
                <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                  <Link to="/" onClick={() => setOpen(false)} data-testid="public-mobile-home" className="flex items-center gap-3 px-4 py-3 rounded-full text-sm text-cocoa/80 hover:bg-champagne">
                    <Sparkles size={16} /> Home
                  </Link>
                  <Link to="/essays" onClick={() => setOpen(false)} data-testid="public-mobile-essays" className="flex items-center gap-3 px-4 py-3 rounded-full text-sm text-cocoa/80 hover:bg-champagne">
                    <BookOpen size={16} /> Editorial
                  </Link>
                  <Link to="/archetypes" onClick={() => setOpen(false)} data-testid="public-mobile-archetypes" className="flex items-center gap-3 px-4 py-3 rounded-full text-sm text-cocoa/80 hover:bg-champagne">
                    <Sparkles size={16} /> Archetypes
                  </Link>
                  <Link to="/research" onClick={() => setOpen(false)} data-testid="public-mobile-research" className="flex items-center gap-3 px-4 py-3 rounded-full text-sm text-cocoa/80 hover:bg-champagne">
                    <BookOpen size={16} /> Research
                  </Link>
                  <Link to="/search" onClick={() => setOpen(false)} data-testid="public-mobile-search" className="flex items-center gap-3 px-4 py-3 rounded-full text-sm text-cocoa/80 hover:bg-champagne">
                    <Search size={16} /> Search
                  </Link>
                  <Link to="/pricing" onClick={() => setOpen(false)} data-testid="public-mobile-pricing" className="flex items-center gap-3 px-4 py-3 rounded-full text-sm text-cocoa/80 hover:bg-champagne">
                    Pricing
                  </Link>
                  <Link to="/about" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-full text-sm text-cocoa/80 hover:bg-champagne">About</Link>
                  <Link to="/faq" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-full text-sm text-cocoa/80 hover:bg-champagne">FAQ</Link>
                  <Link to="/contact" onClick={() => setOpen(false)} data-testid="public-mobile-contact" className="flex items-center gap-3 px-4 py-3 rounded-full text-sm text-cocoa/80 hover:bg-champagne">Contact</Link>
                </nav>
                <div className="p-3 border-t border-champagne space-y-2">
                  <Link to="/login" onClick={() => setOpen(false)} className="block">
                    <button data-testid="public-mobile-login" className="w-full ss-pill ss-pill-ghost h-11">Sign in</button>
                  </Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="block">
                    <button data-testid="public-mobile-register" className="w-full ss-pill ss-pill-gold h-11">Get started</button>
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </>
        )}
      </div>
    </header>
  );
}
